import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { replaceCanvas } from '../../../../store/workflow/workflowSlice';
import { showConfirm } from '../../../common/AppDialog';
import { SplitSpec, splitModesFor, SplitContext, stripSplitSuffix } from '../../utils/splitFlow';
import { typeLabelOf } from '../../nodeCatalog';
import { useTranslation } from 'react-i18next';

interface Props { node: any }

/**
 * 분할처리 그룹 속성 — 자동 생성된 흐름이라 안쪽은 건드리지 않고, **무엇으로 만들어졌는지**와
 * 되돌리는 길(분할 해제)만 보여 준다.
 *
 * 해제하면 그룹과 그 자식을 지우고, 원래의 데이터처리 노드 하나를 같은 자리에 되살린다 —
 * 바깥에서 들어오고 나가던 연결도 그 노드로 다시 잇는다.
 */
const SplitGroupPanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const spec: SplitSpec = node?.data?.split;
  const summary: string = node?.data?.splitSummary || '';
  const baseConfig = node?.data?.config || {};

  const modeLabel = splitModesFor({} as SplitContext).find((m) => m.mode === spec?.mode)?.label || spec?.mode;

  /** 분할 해제 — 그룹·자식을 지우고 데이터처리 노드 하나로 되돌린다 */
  const undoSplit = async () => {
    if (!(await showConfirm(t('property:sg.undoConfirm'), { title: t('property:sg.undoTitle'), okText: t('property:sg.undoOk') }))) return;
    const childIds = new Set(nodes.filter((n: any) => n.parentId === node.id).map((n: any) => n.id));
    const inside = (id: string) => childIds.has(id) || id === node.id;

    const restoredId = `tl_${Date.now().toString(36)}`;
    const restored = {
      id: restoredId,
      type: 'transformLoad',
      position: { ...node.position },
      // 분할처리 적용 때 붙인 접미어를 뗀다 — 그룹을 만든 언어가 지금과 다를 수 있어 지원 언어의 접미어를 모두 본다
      data: { label: stripSplitSuffix(node.data?.label || '') || typeLabelOf('transformLoad'), config: { ...baseConfig, split: undefined } },
      style: { width: 105, height: 56 },
    };

    // 바깥에서 그룹 안으로 들어오던 엣지 → 되살린 노드로, 안에서 바깥으로 나가던 엣지도 마찬가지
    const outerEdges = edges.filter((e: any) => !(inside(e.source) && inside(e.target)));
    const rewired = outerEdges.map((e: any) => ({
      ...e,
      source: inside(e.source) ? restoredId : e.source,
      target: inside(e.target) ? restoredId : e.target,
      id: `e_${inside(e.source) ? restoredId : e.source}_${inside(e.target) ? restoredId : e.target}`,
    }));
    // 같은 짝이 둘 이상이면 하나만 남긴다 (파티션이 여러 개였으므로)
    const seen = new Set<string>();
    const nextEdges = rewired.filter((e: any) => {
      const k = `${e.source}->${e.target}`;
      if (seen.has(k) || e.source === e.target) return false;
      seen.add(k);
      return true;
    });
    const nextNodes = [...nodes.filter((n: any) => !inside(n.id)), restored];
    dispatch(replaceCanvas({ nodes: nextNodes as any, edges: nextEdges as any }));
  };

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="text-sm font-semibold text-gray-800 mb-1">{t('property:sg.title', { mode: modeLabel })}</div>
      <div className="text-xs text-gray-500 mb-3">
        {t('property:sg.intro')}
      </div>

      <div className="rounded border border-gray-200 p-2 mb-3 text-xs text-gray-700">
        <div className="font-medium text-gray-600 mb-1">{t('property:sg.whatItDoes')}</div>
        <div>{summary || t('property:sg.noSummary')}</div>
      </div>

      <table className="w-full text-xs mb-3">
        <tbody>
          <tr className="border-b border-gray-100"><td className="py-1 text-gray-500 w-24">{t('property:sg.count')}</td><td className="py-1 text-gray-800">{spec?.count ?? '-'}</td></tr>
          <tr className="border-b border-gray-100"><td className="py-1 text-gray-500">{t('property:sg.key')}</td><td className="py-1 text-gray-800 font-mono">{spec?.keyColumn || '-'}</td></tr>
          {spec?.dbLinkName && <tr className="border-b border-gray-100"><td className="py-1 text-gray-500">DB link</td><td className="py-1 text-gray-800 font-mono">{spec.dbLinkName}</td></tr>}
          {spec?.tempPrefix && spec?.mode === 'dblink' && <tr className="border-b border-gray-100"><td className="py-1 text-gray-500">{t('property:sg.tempTable')}</td><td className="py-1 text-gray-800 font-mono">{spec.tempPrefix}_0…</td></tr>}
          {spec?.stagingPath && <tr className="border-b border-gray-100"><td className="py-1 text-gray-500">{t('property:sg.stagingFile')}</td><td className="py-1 text-gray-800 font-mono truncate">{spec.stagingPath}</td></tr>}
        </tbody>
      </table>

      <button
        className="px-3 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50"
        onClick={undoSplit}
        title={t('property:sg.undoHint')}
      >
        {t('property:sg.undoTitle')}
      </button>
      <div className="mt-2 text-[0.6875rem] text-gray-400">
        {t('property:sg.changeHint')}
      </div>
    </div>
  );
};

export default SplitGroupPanel;
